// "Alerts, notes and warranties" on the customer record (records.forLocation, BL-010/BL-011): the alerts and
// notes that came over from ServiceTitan or were added here, the preferred technician, and the
// equipment's warranties. The office adds a note or an alert (records.addNote), pins or unpins one
// (records.pinNote; while mirroring, only one added here), sets the preferred technician
// (records.setPreferredTech), records a warranty (records.addWarranty) and voids one recorded wrong
// (records.voidWarranty). Notes are records: never edited. A refusal says the server's words. With
// several addresses the customer's own notes show once, on their own card, and each address's card has
// only its own (history fix 1).

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { newId } from '../shared/ids.ts';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import type { Customer } from '../types.ts';
import {
  alertsOf,
  equipmentLabel,
  longDay,
  notePinnable,
  notesOf,
  warrantyInput,
  warrantyProblems,
  warrantyState,
  warrantyText,
  type Equipment,
  type LocationRecords,
  type NotesPart,
  type RecordNote,
  type WarrantyForm,
} from './records-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useDesign } from '../blueprint/design.ts';
import { RowMore } from '../blueprint/RowMore.tsx';
import './records.css';

type Loc = Customer['locations'][number];

export function AddressRecords({
  customer,
  canEdit,
  today,
}: {
  customer: Customer;
  canEdit: boolean;
  /** The company's today (a warranty starting later reads "Not started"). */
  today: string;
}) {
  const many = customer.locations.length > 1;
  const first = customer.locations[0];
  return (
    <>
      {/* with several addresses, the customer's own notes once, then each address's own (history fix 1) */}
      {many && first && (
        <OneAddress
          location={first}
          customerId={customer.id}
          part="customer"
          canEdit={canEdit}
          today={today}
        />
      )}
      {customer.locations.map((l) => (
        <OneAddress
          key={l.id}
          location={l}
          customerId={customer.id}
          part={many ? 'location' : 'all'}
          canEdit={canEdit}
          today={today}
        />
      ))}
    </>
  );
}

function OneAddress({
  location,
  customerId,
  part,
  canEdit,
  today,
}: {
  location: Loc;
  customerId: string;
  /** 'all' with one address; else the customer's card ('customer') or one address's ('location'). */
  part: NotesPart;
  canEdit: boolean;
  today: string;
}) {
  const [r, setR] = useState<LocationRecords | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // While the company mirrors ServiceTitan, the preferred tech, the warranties and a note's pin are
  // ServiceTitan's; a note added here is Flowology's own (record_notes holds both, st-mirror §2)
  const mirroring = useMirror();
  const load = async () => {
    try {
      setR(await client.call<LocationRecords>('records.forLocation', { locationId: location.id }));
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Could not load this address’s records.'));
    }
  };
  useEffect(() => {
    void load();
  }, [location.id]);
  const done = async (words: string) => {
    setSaid(words);
    await load();
  };
  // Not "At this address": the history's "What we've done at this address" card sits beside it.
  const title =
    part === 'customer'
      ? 'Alerts and notes about the customer'
      : part === 'location'
        ? `Alerts, notes and warranties: ${location.name ?? location.line1}`
        : 'Alerts, notes and warranties';
  return (
    <section className="sheet side-card addr-rec" aria-label={title}>
      <div className="side-head">
        <h2>{title}</h2>
      </div>
      {error && <p className="error small">{error}</p>}
      {!r && !error && <p className="muted small">Loading…</p>}
      {said && (
        <p className="small ok-said" role="status">
          {said}
        </p>
      )}
      {r && (
        <>
          <Notes
            r={r}
            part={part}
            canEdit={canEdit}
            mirroring={mirroring}
            locationId={location.id}
            customerId={customerId}
            onDone={done}
          />
          {part !== 'customer' && (
            <>
              <PreferredTech r={r} canEdit={canEdit && !mirroring} locationId={location.id} onDone={done} />
              <Warranties r={r} canEdit={canEdit && !mirroring} today={today} onDone={done} />
            </>
          )}
          {/* the preferred technician and warranties are ServiceTitan's, and its notes' pins */}
          {canEdit && mirroring && (part !== 'customer' || r.notes.some((n) => n.fromServiceTitan)) && (
            <p className="muted small">{MIRROR_LINE}</p>
          )}
        </>
      )}
    </section>
  );
}

// ── notes and alerts ──

function NoteRow({
  n,
  canEdit,
  mirroring,
  onDone,
}: {
  n: RecordNote;
  /** customers.edit: may pin or unpin a note */
  canEdit: boolean;
  /** while mirroring a ServiceTitan note's pin is ServiceTitan's: shown, not offered */
  mirroring: boolean;
  onDone: (s: string) => Promise<void>;
}) {
  const canPin = notePinnable(n, canEdit, mirroring);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Blueprint: the pin under the note's More, so the part keeps its two actions (DESIGN.md §3)
  const bp = useDesign() === 'blueprint';
  const pin = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('records.pinNote', { noteId: n.id, pinned: !n.pinned });
      await onDone(
        n.pinned
          ? 'Unpinned: it’s a note again.'
          : 'Pinned: it shows as an alert here, on the iPad and when booking.',
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className={n.pinned ? 'rec-alert' : undefined}>
      <span className="rec-body">{n.body}</span>
      <span className="muted small block">
        {n.on === 'customer' ? 'About the customer' : 'At this address'} · {longDay(n.notedAt.slice(0, 10))}
        {n.author ? ` · ${n.author}` : ''}
        {canEdit && !canPin ? ' · From ServiceTitan' : ''}
      </span>
      {canPin &&
        (bp ? (
          <RowMore about="this note">
            <button type="button" className="btn quiet sm" disabled={busy} onClick={() => void pin()}>
              {n.pinned ? 'Unpin' : 'Pin as an alert'}
            </button>
          </RowMore>
        ) : (
          <button type="button" className="btn quiet sm" disabled={busy} onClick={() => void pin()}>
            {n.pinned ? 'Unpin' : 'Pin as an alert'}
          </button>
        ))}
      {error && <span className="error small block">{error}</span>}
    </li>
  );
}

function Notes({
  r,
  part,
  canEdit,
  mirroring,
  locationId,
  customerId,
  onDone,
}: {
  r: LocationRecords;
  part: NotesPart;
  canEdit: boolean;
  /** Pin or unpin: off while mirroring on a note from ServiceTitan, its pin being ServiceTitan's. */
  mirroring: boolean;
  locationId: string;
  customerId: string;
  onDone: (s: string) => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const alerts = alertsOf(r, part);
  const notes = notesOf(r, part);
  return (
    <div className="rec-part">
      {part !== 'customer' && <h3>Alerts and notes</h3>}
      {alerts.length > 0 && (
        <ul className="rec-list" aria-label="Alerts">
          {alerts.map((n) => (
            <NoteRow key={n.id} n={n} canEdit={canEdit} mirroring={mirroring} onDone={onDone} />
          ))}
        </ul>
      )}
      {notes.length > 0 && (
        <ul className="rec-list" aria-label="Notes">
          {notes.map((n) => (
            <NoteRow key={n.id} n={n} canEdit={canEdit} mirroring={mirroring} onDone={onDone} />
          ))}
        </ul>
      )}
      {!alerts.length && !notes.length && <p className="muted small">No alerts or notes yet.</p>}
      {canEdit &&
        (adding ? (
          <AddNote
            locationId={locationId}
            customerId={customerId}
            part={part}
            onClose={() => setAdding(false)}
            onDone={async (s) => {
              setAdding(false);
              await onDone(s);
            }}
          />
        ) : (
          <button type="button" className="btn quiet sm" onClick={() => setAdding(true)}>
            Add a note
          </button>
        ))}
    </div>
  );
}

function AddNote({
  locationId,
  customerId,
  part,
  onClose,
  onDone,
}: {
  locationId: string;
  customerId: string;
  /** A split card adds only its own kind of note: the customer's, or this address's. */
  part: NotesPart;
  onClose: () => void;
  onDone: (s: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [on, setOn] = useState<'location' | 'customer'>(part === 'customer' ? 'customer' : 'location');
  const [pinned, setPinned] = useState(false);
  const [noteId] = useState(newId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('records.addNote', {
        noteId,
        on,
        entityId: on === 'location' ? locationId : customerId,
        body: body.trim(),
        pinned,
      });
      await onDone(pinned ? 'Alert added: it shows here, on the iPad and when booking.' : 'Note added.');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rec-form">
      <label>
        <span className="flabel">The note</span>
        <textarea
          className="field"
          rows={3}
          maxLength={4000}
          value={body}
          placeholder="Gate code changes monthly; ask at the office"
          onChange={(e) => setBody(e.target.value)}
        />
      </label>
      {part === 'all' && (
        <div className="pills" role="group" aria-label="It’s about">
          <button
            type="button"
            className="pill"
            aria-pressed={on === 'location'}
            onClick={() => setOn('location')}
          >
            This address
          </button>
          <button
            type="button"
            className="pill"
            aria-pressed={on === 'customer'}
            onClick={() => setOn('customer')}
          >
            The customer
          </button>
        </div>
      )}
      <label className="checkrow">
        <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} /> Show it as an
        alert (on the iPad and when booking)
      </label>
      <div className="row">
        <button type="button" className="btn soft" onClick={onClose}>
          Cancel
        </button>
        <SaveButton
          busy={busy}
          busyLabel="Adding…"
          error={error}
          disabled={!body.trim()}
          onClick={() => void save()}
        >
          {pinned ? 'Add the alert' : 'Add the note'}
        </SaveButton>
      </div>
    </div>
  );
}

// ── the preferred technician ──

function PreferredTech({
  r,
  canEdit,
  locationId,
  onDone,
}: {
  r: LocationRecords;
  canEdit: boolean;
  locationId: string;
  onDone: (s: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [techs, setTechs] = useState<{ id: string; name: string }[] | null>(null);
  const [pick, setPick] = useState(r.preferredTechnician?.staffId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!editing || techs) return;
    void client
      .call<{ staff: { id: string; displayName: string }[] }>('staff.list', { techniciansOnly: true })
      .then((x) => setTechs(x.staff.map((s) => ({ id: s.id, name: s.displayName }))))
      .catch(() => setTechs([]));
  }, [editing, techs]);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const out = await client.call<{ preferredTechnician: { name: string } | null }>(
        'records.setPreferredTech',
        {
          locationId,
          staffId: pick || null,
        },
      );
      setEditing(false);
      await onDone(
        out.preferredTechnician
          ? `${out.preferredTechnician.name} is offered first when booking here.`
          : 'No preferred technician here now.',
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rec-part">
      <h3>Preferred technician</h3>
      {!editing ? (
        <div className="row">
          <span>{r.preferredTechnician?.name ?? <span className="muted">None</span>}</span>
          {canEdit && (
            <button
              type="button"
              className="btn quiet sm"
              aria-label="Change the preferred technician"
              onClick={() => {
                setPick(r.preferredTechnician?.staffId ?? '');
                setEditing(true);
              }}
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <div className="rec-form">
          <label>
            <span className="flabel">Technician</span>
            <select
              className="field"
              value={pick}
              disabled={!techs}
              onChange={(e) => setPick(e.target.value)}
            >
              <option value="">{techs ? 'No preference' : 'Loading the technicians…'}</option>
              {(techs ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <div className="row">
            <button type="button" className="btn soft" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <SaveButton busy={busy} busyLabel="Saving…" error={error} onClick={() => void save()}>
              Save
            </SaveButton>
          </div>
        </div>
      )}
    </div>
  );
}

// ── equipment and warranties ──

function Warranties({
  r,
  canEdit,
  today,
  onDone,
}: {
  r: LocationRecords;
  canEdit: boolean;
  today: string;
  onDone: (s: string) => Promise<void>;
}) {
  return (
    <div className="rec-part">
      <h3>Warranties</h3>
      {!r.equipment.length ? (
        <p className="muted small">
          No equipment on record here. Add it from Location, then its warranty here.
        </p>
      ) : (
        <ul className="rec-list" aria-label="Equipment">
          {r.equipment.map((e) => (
            <OneEquipment key={e.id} e={e} canEdit={canEdit} today={today} onDone={onDone} />
          ))}
        </ul>
      )}
    </div>
  );
}

function OneEquipment({
  e,
  canEdit,
  today,
  onDone,
}: {
  e: Equipment;
  canEdit: boolean;
  today: string;
  onDone: (s: string) => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [voiding, setVoiding] = useState<string | null>(null);
  const name = equipmentLabel(e).replace(/^the /, '');
  return (
    <li>
      <b className="cap">{name}</b>
      {e.installedOn && <span className="muted small"> · installed {longDay(e.installedOn)}</span>}
      {e.retiredAt && (
        <span className="muted small block">
          Retired {longDay(e.retiredAt.slice(0, 10))}
          {e.retiredReason ? `: ${e.retiredReason}` : ''}. Its warranties no longer count.
        </span>
      )}
      {e.warranties.length ? (
        <ul className="small rec-warr">
          {e.warranties.map((w) => (
            <li key={w.id} className={w.voided ? 'voided' : undefined}>
              <span>{warrantyText(w)}</span>{' '}
              <span className={`chip say${w.inForce ? ' ok' : ''}`}>{warrantyState(w, today)}</span>
              {w.terms && <span className="muted block">{w.terms}</span>}
              {w.voided && <span className="muted block">Voided: {w.voided.reason}</span>}
              {canEdit && !w.voided && !e.retiredAt && voiding !== w.id && (
                <button type="button" className="btn quiet sm" onClick={() => setVoiding(w.id)}>
                  Void (recorded wrong)
                </button>
              )}
              {voiding === w.id && (
                <VoidWarranty
                  warrantyId={w.id}
                  onClose={() => setVoiding(null)}
                  onDone={async (s) => {
                    setVoiding(null);
                    await onDone(s);
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <span className="muted small block">No warranty recorded.</span>
      )}
      {canEdit &&
        !e.retiredAt &&
        (adding ? (
          <AddWarranty
            equipment={e}
            today={today}
            onClose={() => setAdding(false)}
            onDone={async (s) => {
              setAdding(false);
              await onDone(s);
            }}
          />
        ) : (
          <button
            type="button"
            className="btn quiet sm"
            aria-label={`Record a warranty on ${equipmentLabel(e)}`}
            onClick={() => setAdding(true)}
          >
            Record a warranty
          </button>
        ))}
    </li>
  );
}

function AddWarranty({
  equipment,
  today,
  onClose,
  onDone,
}: {
  equipment: Equipment;
  today: string;
  onClose: () => void;
  onDone: (s: string) => Promise<void>;
}) {
  const [f, setF] = useState<WarrantyForm>({
    kind: 'manufacturer',
    startsOn: equipment.installedOn ?? today,
    endsOn: '',
    lifetime: false,
    terms: '',
  });
  const [warrantyId] = useState(newId);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useSaveKey(JSON.stringify(f));
  const problems = warrantyProblems(f);
  const save = async () => {
    setTried(true);
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('records.addWarranty', warrantyInput(equipment.id, warrantyId, f), {
        idempotencyKey: key.key,
      });
      await onDone(
        `Warranty recorded on ${equipmentLabel(equipment)}. Booking here says so while it’s in force.`,
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rec-form">
      <label>
        <span className="flabel">Kind</span>
        <select
          className="field"
          value={f.kind}
          onChange={(e) => setF({ ...f, kind: e.target.value as WarrantyForm['kind'] })}
        >
          <option value="manufacturer">Manufacturer’s</option>
          <option value="labour">Labour (ours)</option>
        </select>
      </label>
      <label>
        <span className="flabel">From</span>
        <input
          className="field"
          type="date"
          value={f.startsOn}
          onChange={(e) => setF({ ...f, startsOn: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Until</span>
        <input
          className="field"
          type="date"
          value={f.endsOn}
          disabled={f.lifetime}
          onChange={(e) => setF({ ...f, endsOn: e.target.value })}
        />
      </label>
      <label className="checkrow">
        <input
          type="checkbox"
          checked={f.lifetime}
          onChange={(e) => setF({ ...f, lifetime: e.target.checked })}
        />{' '}
        Lifetime (it never ends)
      </label>
      <label>
        <span className="flabel">Terms (optional)</span>
        <input
          className="field"
          maxLength={1000}
          value={f.terms}
          placeholder="Tank only; parts, not labour"
          onChange={(e) => setF({ ...f, terms: e.target.value })}
        />
      </label>
      {tried && problems.length > 0 && (
        <ul className="error small" role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div className="row">
        <button type="button" className="btn soft" onClick={onClose}>
          Cancel
        </button>
        <SaveButton busy={busy} busyLabel="Recording…" error={error} onClick={() => void save()}>
          Record the warranty
        </SaveButton>
      </div>
    </div>
  );
}

function VoidWarranty({
  warrantyId,
  onClose,
  onDone,
}: {
  warrantyId: string;
  onClose: () => void;
  onDone: (s: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('records.voidWarranty', { warrantyId, reason: reason.trim() });
      await onDone(
        'Voided. It stays in the history and no longer counts when booking; record the right one.',
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rec-form">
      <label>
        <span className="flabel">What was wrong (required)</span>
        <input
          className="field"
          maxLength={500}
          value={reason}
          placeholder="The year was mistyped"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <div className="row">
        <button type="button" className="btn soft" onClick={onClose}>
          Keep it
        </button>
        <SaveButton
          busy={busy}
          busyLabel="Voiding…"
          error={error}
          disabled={!reason.trim()}
          onClick={() => void save()}
        >
          Void the warranty
        </SaveButton>
      </div>
    </div>
  );
}
